import type { ReactElement } from 'react';
import { RASTER_WAHLEN } from '@schnitt/shared';
import { useEditor } from '../store';
import { Sheet, amSchreibtisch } from './Sheet';
import { Icon } from './Icon';
import type { SheetName } from './Toolbar';

/**
 * Was nicht in die fünf Knöpfe passt (21. September).
 *
 * Sascha: „Bitte räume das untere Menü auf, sodass wir im Schnitt immer nur so
 * fünf Buttons haben.“ Weggefallen ist nichts -- es steht hier, und zwar als
 * Zeile mit einem Satz dazu. Ein Symbol in einer Leiste muss man deuten; eine
 * Zeile mit „Messgeräte -- Wellenform und Vektorskop über dem Bild“ liest man.
 *
 * Eine Zeile, ein Tipp, Blatt zu: wer hierher kommt, will eines von fünf
 * Dingen und nicht bleiben. Nur Raster und Pegelmesser bleiben offen: man
 * sieht die Wirkung im Bild dahinter.
 */
export function WerkzeugeSheet({
  onClose,
  onOpen,
  onBefehle,
}: {
  onClose: () => void;
  onOpen: (sheet: SheetName) => void;
  onBefehle: () => void;
}): ReactElement {
  const raster = useEditor((s) => s.raster);
  const setRaster = useEditor((s) => s.setRaster);
  const toast = useEditor((s) => s.toast);
  const rasterJetzt = RASTER_WAHLEN.find((w) => w.id === raster)!;
  const pegelWahl = useEditor((s) => s.setPegelWahl);
  const pegelAn = (useEditor((s) => s.pegelWahl) ?? (amSchreibtisch() ? 'an' : 'aus')) === 'an';

  const zeile = (
    id: string,
    icon: string,
    titel: string,
    satz: string,
    tun: () => void,
    extra?: { bleibt?: boolean; wert?: string },
  ): ReactElement => (
    <button
      key={id}
      className="row"
      data-werkzeug={id}
      style={{ textAlign: 'left', width: '100%' }}
      onClick={() => {
        tun();
        if (!extra?.bleibt) onClose();
      }}
    >
      <Icon name={icon} />
      <div className="grow">
        <div className="title">{titel}</div>
        <div className="sub">{satz}</div>
      </div>
      {extra?.wert ? <span className="badge">{extra.wert}</span> : null}
    </button>
  );

  return (
    <Sheet title="Werkzeuge" onClose={onClose}>
      <div className="list" data-werkzeuge="6">
        {/* Wer in ein anderes Blatt führt, sagt hier nicht auch noch „zu“:
            das Blatt verschwindet von selbst, sobald ein anderes offen ist. */}
        {zeile('brandKit', 'brand', 'Marke', 'Farben, Schrift und Logo für alle Bauchbinden', () => onOpen('brandKit'), { bleibt: true })}
        {zeile('transcript', 'captions', 'Untertitel', 'Abschrift, Untertitel setzen, ein- und ausgeben', () => onOpen('transcript'), { bleibt: true })}
        {zeile('messgeraete', 'messgeraet', 'Messgeräte', 'Wellenform und Vektorskop über dem Bild', () => onOpen('messgeraete'), { bleibt: true })}
        {zeile(
          'raster',
          'raster',
          'Raster und Schutzbereich',
          'Hilfslinien über dem Bild -- Drittel, Schutzbereich oder beides',
          () => {
            const naechste = RASTER_WAHLEN[(RASTER_WAHLEN.findIndex((w) => w.id === raster) + 1) % RASTER_WAHLEN.length]!;
            setRaster(naechste.id);
            toast('info', naechste.id === 'aus' ? 'Raster aus.' : `Raster: ${naechste.label}.`);
          },
          { bleibt: true, wert: rasterJetzt.label },
        )}
        {zeile(
          'pegel',
          'volume',
          'Pegelmesser',
          'Links und rechts in dB, neben dem Bild',
          () => {
            pegelWahl(pegelAn ? 'aus' : 'an');
            toast('info', pegelAn ? 'Pegelmesser aus.' : 'Pegelmesser an.');
          },
          { bleibt: true, wert: pegelAn ? 'An' : 'Aus' },
        )}
        {zeile('suchen', 'suchen', 'Suchen', 'Jeden Befehl über seinen Namen finden (⌘K)', onBefehle)}
      </div>
    </Sheet>
  );
}
